import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useLayoutEffect,
} from "react";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useQueryClient } from "@tanstack/react-query";
import { MessageType } from "@common/types/chat";
import { Button } from "@design-system/Button/Button";
import { CloseIcon } from "@icons";
import {
    InfiniteScrollContainer,
    InfiniteScrollHandle,
} from "@design-system/InfiniteScrollContainer/InfiniteScrollContainer";
import { useNatsSubscription } from "@hooks/useNatsSubscription";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useScope } from "@state/scopeContext.lib";
import {
    useGetOrCreateSubthread,
    useMessages,
    useSubthreadMessages,
    useSendSubthreadMessage,
    subthreadQueryKey,
    flattenMessagesPages,
} from "@api/chat.api";
import { pluralizeWithCount } from "@common/helpers/string";
import {
    useSpreadsheetRefContextMaybe,
    useDraftRefsForTarget,
} from "@components/tasks/SpreadsheetRefContext";
import {
    useTransactionRefContextMaybe,
    useDraftTransactionRefsForTarget,
} from "@components/tasks/TransactionRefContext";
import {
    useCounterpartyRefContextMaybe,
    useDraftCounterpartyRefsForTarget,
} from "@components/tasks/CounterpartyRefContext";
import {
    useInvoiceRefContextMaybe,
    useDraftInvoiceRefsForTarget,
} from "@components/tasks/InvoiceRefContext";
import {
    useDocumentRefContextMaybe,
    useDraftDocumentRefsForTarget,
} from "@components/tasks/DocumentRefContext";
import {
    useChatComposerDrop,
    useChatComposerDropZone,
} from "@components/tasks/chatDrag/useChatComposerDrop";
import { MessageFormSubmitParams, MessageForm } from "./MessageForm";
import { MessageBubble } from "./MessageBubble";
import styles from "./SubthreadPanel.module.scss";

interface SubthreadPanelProps {
    rootMessageId: string;
    threadId: string;
    taskId?: string;
    onClose: () => void;
}

export const SubthreadPanel = ({
    rootMessageId,
    threadId,
    taskId,
    onClose,
}: SubthreadPanelProps) => {
    const workspace = useActiveWorkspace();
    const { organizationId } = useScope();
    const queryClient = useQueryClient();
    const toaster = useToaster();

    const {
        data: subthread,
        isLoading: isCreating,
        isError: isSubthreadError,
        mutate: getOrCreateSubthread,
    } = useGetOrCreateSubthread(threadId, rootMessageId);

    useEffect(() => {
        getOrCreateSubthread();
    }, [getOrCreateSubthread]);

    const subthreadId = subthread?.id;

    // Load messages
    const {
        data: messagesPages,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useSubthreadMessages(subthreadId);

    const messages = useMemo(
        () => flattenMessagesPages(messagesPages?.pages),
        [messagesPages],
    );
    const { data: threadMessagesPages } = useMessages(threadId);
    const rootMessage = useMemo(() => {
        const threadMessages = flattenMessagesPages(threadMessagesPages?.pages);

        return (
            threadMessages.find((message) => message.id === rootMessageId) ??
            null
        );
    }, [threadMessagesPages, rootMessageId]);
    const rootMessageOrganizationId = rootMessage?.organizationId ?? null;
    // When rootMessage is not in the parent thread cache, fall back to the
    // current org scope to prevent leaking an internal reply as external.
    const isRootMessageLoaded = rootMessage !== null;
    const isInternalRootMessage = isRootMessageLoaded
        ? Boolean(rootMessageOrganizationId)
        : Boolean(organizationId);
    const replyCount = rootMessage?.replyCount ?? subthread?.replyCount ?? 0;

    const scrollRef = useRef<InfiniteScrollHandle>(null);
    const prevLastMessageIdRef = useRef<string | undefined>(undefined);
    useLayoutEffect(() => {
        const lastMessage =
            messages.length > 0 ? messages[messages.length - 1] : undefined;
        const prevLastId = prevLastMessageIdRef.current;
        prevLastMessageIdRef.current = lastMessage?.id;

        // Auto-scroll only when the bottom-most message changes (new reply or
        // initial load), not when older messages are prepended via pagination.
        if (lastMessage && lastMessage.id !== prevLastId) {
            scrollRef.current?.scrollToBottom();
        }
    }, [messages]);

    // Real-time updates via NATS (same channel as parent thread)
    useNatsSubscription(
        workspace?.id
            ? `workspaces.${workspace.id}.notifications.threads.${threadId}`
            : null,
        {
            onMessage: () => {
                if (!subthreadId || !workspace?.id) {
                    return;
                }
                queryClient.invalidateQueries({
                    queryKey: subthreadQueryKey(
                        workspace.id,
                        subthreadId,
                        organizationId,
                    ),
                });
            },
        },
    );

    const { mutateAsync: sendMessage } = useSendSubthreadMessage();
    const plRefCtx = useSpreadsheetRefContextMaybe();
    const trxRefCtx = useTransactionRefContextMaybe();
    const cpRefCtx = useCounterpartyRefContextMaybe();
    const invRefCtx = useInvoiceRefContextMaybe();
    const docRefCtx = useDocumentRefContextMaybe();
    const clearDraftRefs = plRefCtx?.clearDraftRefs;
    const subthreadDraftRefs = useDraftRefsForTarget("subthread");
    const subthreadDraftTransactionRefs =
        useDraftTransactionRefsForTarget("subthread");
    const subthreadDraftCounterpartyRefs =
        useDraftCounterpartyRefsForTarget("subthread");
    const subthreadDraftInvoiceRefs = useDraftInvoiceRefsForTarget("subthread");
    const subthreadDraftDocumentRefs =
        useDraftDocumentRefsForTarget("subthread");
    const composerDrop = useChatComposerDrop("subthread");
    const setDropZoneRef = useChatComposerDropZone("subthread");

    const onSend = useCallback(
        async ({
            content,
            organizationId: messageOrganizationId,
            spreadsheetReferences,
            transactionReferences,
            counterpartyReferences,
            invoiceReferences,
            documentReferences,
            financialDocumentIds,
        }: MessageFormSubmitParams) => {
            if (!subthreadId) {
                return;
            }
            const effectiveOrganizationId = isInternalRootMessage
                ? (rootMessageOrganizationId ?? organizationId)
                : messageOrganizationId;
            try {
                await sendMessage({
                    subthreadId,
                    threadId,
                    rootMessageId,
                    content,
                    type: MessageType.SLATE,
                    organizationId: effectiveOrganizationId,
                    spreadsheetReferences,
                    transactionReferences,
                    counterpartyReferences,
                    invoiceReferences,
                    documentReferences,
                    financialDocumentIds,
                });
                clearDraftRefs?.("subthread");
                trxRefCtx?.clearDraftRefs("subthread");
                cpRefCtx?.clearDraftRefs("subthread");
                invRefCtx?.clearDraftRefs("subthread");
                docRefCtx?.clearDraftRefs("subthread");
                scrollRef.current?.scrollToBottom();
            } catch (error) {
                // Draft refs and attachment chips are intentionally preserved
                // on error so the user can retry without re-selecting cells or
                // re-uploading files. Re-throw so MessageForm keeps the composer.
                toaster.error("Failed to send reply. Please try again.");
                throw error;
            }
        },
        [
            subthreadId,
            threadId,
            rootMessageId,
            sendMessage,
            isInternalRootMessage,
            rootMessageOrganizationId,
            organizationId,
            toaster,
            clearDraftRefs,
            trxRefCtx,
            cpRefCtx,
            invRefCtx,
            docRefCtx,
        ],
    );

    return (
        <div className={styles.subthreadPanel} ref={setDropZoneRef}>
            <div className={styles.header}>
                <span className={styles.title}>Thread</span>
                <Button
                    iconOnly
                    variant="primary"
                    size="sm"
                    onClick={onClose}
                    aria-label="Close thread"
                >
                    <CloseIcon />
                </Button>
            </div>

            {isSubthreadError && (
                <div className={styles.loading}>
                    Failed to load thread. Please close and try again.
                </div>
            )}
            {!isSubthreadError && isCreating && (
                <div className={styles.loading}>Loading thread…</div>
            )}
            {!isSubthreadError && !isCreating && (
                <>
                    <div className={styles.messagesContainer}>
                        {rootMessage && (
                            <div>
                                <MessageBubble
                                    message={rootMessage}
                                    isThreadContext
                                    showReplyFooter={false}
                                    taskId={taskId}
                                />
                                {replyCount > 0 ? (
                                    <div
                                        className={
                                            styles.repliesDividerContainer
                                        }
                                    >
                                        <span className={styles.text}>
                                            {pluralizeWithCount(
                                                "reply",
                                                replyCount,
                                            )}
                                        </span>
                                        <div className={styles.dividerLine} />
                                    </div>
                                ) : null}
                            </div>
                        )}
                        {messages.length > 0 ? (
                            <div className={styles.repliesScroll}>
                                <InfiniteScrollContainer
                                    ref={scrollRef}
                                    hasNextPage={hasNextPage}
                                    loadMore={fetchNextPage}
                                    isFetchingMoreMessages={isFetchingNextPage}
                                    direction="up"
                                    height="100%"
                                >
                                    {messages.map((message) => (
                                        <MessageBubble
                                            key={message.id}
                                            message={message}
                                            isThreadContext
                                            taskId={taskId}
                                        />
                                    ))}
                                </InfiniteScrollContainer>
                            </div>
                        ) : (
                            <div className={styles.empty} />
                        )}
                    </div>

                    <div className={styles.inputContainer}>
                        <MessageForm
                            onSubmit={onSend}
                            autoFocus
                            placeholder="Reply in thread…"
                            initialOrganizationScopedMessage={
                                isInternalRootMessage
                            }
                            hideOrganizationScopePicker={isInternalRootMessage}
                            draftRefs={subthreadDraftRefs.draftRefs}
                            onRemoveDraftRef={
                                subthreadDraftRefs.onRemoveDraftRef
                            }
                            draftTransactionRefs={
                                subthreadDraftTransactionRefs.draftRefs
                            }
                            onRemoveDraftTransactionRef={
                                subthreadDraftTransactionRefs.onRemoveDraftRef
                            }
                            draftCounterpartyRefs={
                                subthreadDraftCounterpartyRefs.draftRefs
                            }
                            onRemoveDraftCounterpartyRef={
                                subthreadDraftCounterpartyRefs.onRemoveDraftRef
                            }
                            draftInvoiceRefs={
                                subthreadDraftInvoiceRefs.draftRefs
                            }
                            onRemoveDraftInvoiceRef={
                                subthreadDraftInvoiceRefs.onRemoveDraftRef
                            }
                            draftDocumentRefs={
                                subthreadDraftDocumentRefs.draftRefs
                            }
                            onRemoveDraftDocumentRef={
                                subthreadDraftDocumentRefs.onRemoveDraftRef
                            }
                            resetKey={subthreadId}
                            isDropTarget={composerDrop.isDropTarget}
                            focusRef={composerDrop.focusRef}
                        />
                    </div>
                </>
            )}
        </div>
    );
};
